/**
 * DaVinci OccupationInfo extractor.
 *
 * Each accommodation service line in a SUCCESSFUL BA (Booking Action) response carries an
 * `OccupationInfo` block describing the hotel's per-room occupancy rules:
 *
 *   "OccupationInfo": { MinimumPersons, MaximumPersons, MinimumAdults, MaximumAdults,
 *                       MaximumChilds, MaximumBabys }
 *
 * This is NOT present on the 670 band-probe response (which is a deliberate failure). We
 * capture the constraints from BA #1 now and keep them ready; party validation against
 * them is deferred (framework §9.7 — see the TODO at the call site).
 *
 * See claude/specs/ChildVsInfant/IMPLEMENTATION-SPEC.md §13.
 */

/** Per-service occupancy constraints from a BA response's `OccupationInfo`. */
export interface OccupationConstraints {
  /** DaVinci `Service.Index` (1-based) the constraints belong to. */
  serviceIndex: number;
  minimumPersons?: number;
  maximumPersons?: number;
  minimumAdults?: number;
  maximumAdults?: number;
  maximumChilds?: number;
  maximumBabys?: number;
}

/** Coerce an unknown value to a finite number, or `undefined`. */
function toNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/**
 * Extract per-service occupancy constraints from a BA booking response.
 *
 * @param bookingResponse - The `Booking` object from a DaVinci BA response.
 * @returns One entry per service line that carries an `OccupationInfo` block.
 */
export function extractOccupationConstraints(
  bookingResponse: Record<string, unknown>
): OccupationConstraints[] {
  const services = bookingResponse.Services as Array<Record<string, unknown>> | undefined;
  if (!Array.isArray(services)) return [];

  const result: OccupationConstraints[] = [];
  services.forEach((service, arrayIndex) => {
    const info = service.OccupationInfo as Record<string, unknown> | undefined;
    if (!info || typeof info !== 'object') return;

    // Service.Index is 1-based; fall back to array position + 1 if it's missing.
    const serviceIndex = toNumber(service.Index) ?? arrayIndex + 1;

    result.push({
      serviceIndex,
      minimumPersons: toNumber(info.MinimumPersons),
      maximumPersons: toNumber(info.MaximumPersons),
      minimumAdults: toNumber(info.MinimumAdults),
      maximumAdults: toNumber(info.MaximumAdults),
      maximumChilds: toNumber(info.MaximumChilds),
      maximumBabys: toNumber(info.MaximumBabys),
    });
  });

  return result;
}
